<template>
  <slot
    v-if="selectedVariant"
    :name="selectedVariant"
    :variant="selectedVariant"
    :experiment="experiment"
  >
    <slot></slot>
  </slot>
</template>

<script setup lang="ts">
import { useUserStore } from '@rendora/common/store';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';

// 变体配置接口
export interface Variant {
  name: string; // 变体名称，如 'A', 'B', 'C'
  weight: number; // 权重，范围 0-100，所有变体的权重之和应该为 100
}

// 权重分配算法函数类型
export type AssignAlgorithm = (experiment: string, userId: string, variants: Variant[]) => string;

// 组件 Props
export interface AbTestProps {
  // 实验名称，用于区分不同的 AB 测试
  experiment: string;
  // 变体配置数组，如果不提供则默认使用 50% A 50% B
  variants?: Variant[];
  // 可选的用户ID，如果不提供则使用 store 中的 user_id
  userId?: string;
  // 是否上报埋点，默认为 false
  track?: boolean;
  // 自定义权重分配算法，如果不提供则使用默认的基于 userid 奇偶性的算法（奇数是A，偶数是B）
  assignAlgorithm?: AssignAlgorithm;
}

const props = withDefaults(defineProps<AbTestProps>(), {
  track: false,
  variants: () => [
    { name: 'A', weight: 50 },
    { name: 'B', weight: 50 },
  ],
});

// 获取 user 信息
const userStore = useUserStore();
const { user } = storeToRefs(userStore);

// 当前选中的变体
const selectedVariant = ref<string | null>(null);

// 获取用户标识符
const getUserId = (): string => {
  // 优先使用 props 中的 userId，否则使用 store 中的 user_id
  if (props.userId) {
    return props.userId;
  }
  return user.value?.user_id?.toString() || '';
};

// 默认权重分配算法：基于 userid 的奇偶性（奇数是A，偶数是B）
const assignVariant = (experiment: string, userId: string, variants: Variant[]): string => {
  const userIdNum = parseInt(userId, 10);

  return userIdNum % 2 === 0 ? 'A' : 'B';
};

// 初始化
onMounted(() => {
  if (import.meta.server) {
    return;
  }

  // 验证变体配置
  if (!props.variants || props.variants.length === 0) {
    console.error(`[AB Test] 实验 "${props.experiment}" 没有配置变体`);
    return;
  }

  // 如果使用自定义算法，验证权重总和（可选，仅警告）
  if (props.assignAlgorithm) {
    const totalWeight = props.variants.reduce((sum, v) => sum + v.weight, 0);
    if (Math.abs(totalWeight - 100) > 0.01) {
      console.warn(
        `[AB Test] 实验 "${props.experiment}" 的权重总和为 ${totalWeight}，建议调整为 100`,
      );
    }
  }

  // 分配变体：使用自定义算法或默认算法
  const userId = getUserId();
  const variant = props.assignAlgorithm
    ? props.assignAlgorithm(props.experiment, userId, props.variants)
    : assignVariant(props.experiment, userId, props.variants);
  selectedVariant.value = variant;
});

// 暴露给父组件的方法和属性
defineExpose({
  selectedVariant,
  experiment: props.experiment,
});
</script>
